<template>
  <RCard class="cost-allocation">
    <Grid>
      <Grid gaf="column" justify-content="start" align-items="center" gap="var(--r-space-md)">
        <div class="text-subtitle-1 text-secondary-2">Cost allocation and Stakeholders</div>

        <RDropdown
          v-model="selectedPeriodId"
          :options="periodOptions"
          placeholder="Select subscription period"
          emit-value
          map-options
          style="width: 200px"
          no-label
          no-hint
        />
      </Grid>

      <AgGrid
        v-if="departments"
        ref="agGrid"
        :context="context"
        :options="gridOptions"
        :row-data="stakes"
        @cell-value-changed="cellValueChanged"
      />

      <RBtn type="text" size="small" class="q-mt-md">
        Add {{ departmentLabel.singular.toLowerCase() }}
        <TriangleIcon class="q-ml-xs" />
        <RPopup :items="popupItems" @popup-item="addDepartment" />
      </RBtn>
    </Grid>
  </RCard>
</template>

<script>
import * as apiTo from '@/modular-api'
// Mixins
import gridOptions from './grid-options'
// Libraries
import moment from 'moment'
import { mapGetters } from 'vuex'
// Components
import AgGrid from '@/ag-grid/AgGrid'
import TriangleIcon from '@/ui/icons/TriangleIcon'
import RPopup from '@/ui/RPopup'

export default {
  components: {
    AgGrid,
    TriangleIcon,
    RPopup
  },

  mixins: [gridOptions],

  props: {
    periods: Array,
    subId: [Number, String]
  },

  data() {
    return {
      departments: null,
      stakeholders: null,
      costCenters: null,
      selectedPeriodId: null,
      stakes: null,
      context: {}
    }
  },

  computed: {
    ...mapGetters('subscription-show', ['subscription']),
    ...mapGetters('entity-labels', ['departmentLabel', 'costCenterLabel']),

    periodOptions() {
      return this.periods.map(period => ({
        label: moment(period.start_date, 'YYYY-MM-DD').format('YYYY'),
        value: period.id
      }))
    },

    popupItems() {
      if (!this.departments) return []

      return this.departments.map(dep => ({
        name: dep.name,
        label: dep.name,
        id: dep.id
      }))
    }
  },

  watch: {
    selectedPeriodId: {
      handler(val) {
        if (!val) {
          const { current, next } = this.subscription
          if (current) this.getStakes(current.id)
          else this.getStakes(next.id)
        } else {
          this.getStakes(val)
        }
      },
      immediate: true
    }
  },

  created() {
    this.context = { costAllocationCmp: this }
    this.getDepartmentsStakeholdersAndCostCenters()
  },

  methods: {
    getDepartmentsStakeholdersAndCostCenters() {
      Promise.all([apiTo.GET_DEPARTMENTS(), apiTo.GET_STAKEHOLDERS('departments'), apiTo.GET_COST_CENTERS()]).then(
        ([{ data: departmentsResponse }, { data: stakeholdersResponse }, { data: costCentersResponse }]) => {
          this.departments = departmentsResponse.result
          this.stakeholders = stakeholdersResponse.result
          this.costCenters = costCentersResponse.result
        }
      )
    },

    getStakes(periodId) {
      apiTo.GET_STAKES_FOR_SUBSCRIPTION_PERIOD(periodId).then(({ data }) => {
        this.stakes = data.result
        this.selectedPeriodId = periodId
      })
    },

    deleteDepartment(row) {
      apiTo
        .DELETE_SUBSCRIPTION_PERIOD_DEPARTMENT(this.selectedPeriodId, row.id)
        .then(() => this.getStakes(this.selectedPeriodId))
    },

    addDepartment({ id: depId }) {
      const systemCostCenter = this.costCenters.find(c => c.system)

      apiTo
        .ADD_DEPARTMENT_TO_SUBSCRIPTION_PERIOD(this.selectedPeriodId, depId, null, systemCostCenter.id)
        .then(() => this.getStakes(this.selectedPeriodId))
    },

    didValueChanged(old, newVal) {
      if (typeof old === 'string' && typeof newVal === 'number') return Number(old) !== Number(newVal)
      return old !== newVal
    },

    cellValueChanged(event) {
      if (!this.didValueChanged(event.oldValue, event.newValue)) return

      const coldId = event.column.colId
      let value = event.newValue
      let key = coldId

      if (coldId === 'percentage') {
        value = value.toFixed(8)
      } else if (['cost_center', 'department'].includes(coldId)) {
        value = value.id
      }

      if (coldId === 'department') key = 'department_id'
      if (coldId === 'cost_center') key = 'cost_center_id'

      apiTo
        .UPDATE_SUBSCRIPTION_PERIOD_STAKE(this.selectedPeriodId, event.data.department.id, {
          [key]: String(value),
          department_stake_id: event.data.id
        })
        .finally(() => this.getStakes(this.selectedPeriodId))
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/mixins';

.cost-allocation {
  @include table-with-no-filters;
}
</style>
